import React, { useState, useEffect, useMemo } from 'react';
import { cpdAPI, API_BASE_URL } from '../services/api';
import PDFPreviewModal from '../components/PDFPreviewModal';
import './CpdApp.css';

const KNOWLEDGE_AREA_LABELS = {
  technical: 'Technical Competence',
  client_care: 'Client Care & Practice',
  regulatory: 'Regulatory Compliance',
  professionalism: 'Professionalism & Ethics',
};

const KNOWLEDGE_AREA_COLOURS = {
  technical: 'var(--info-color, #599BE3)',
  client_care: 'var(--success-color, #91CF9B)',
  regulatory: 'var(--secondary-color, #f0a500)',
  professionalism: 'var(--primary-color, #072040)',
};

const CpdNativeVideo = ({ resourceId, title }) => {
  const [videoSrc, setVideoSrc] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchVideoUrl = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(
          `${API_BASE_URL}/api/resources/download/${resourceId}?direct_url=true`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error('Failed to get video URL');
        const data = await response.json();
        if (!data.url) throw new Error('No URL in response');
        setVideoSrc(data.url);
        setLoading(false);
      } catch (err) {
        console.error('Video load error:', err);
        setError('Failed to load video');
        setLoading(false);
      }
    };
    fetchVideoUrl();
  }, [resourceId]);

  if (loading) {
    return (
      <div className="cpd-video-container" style={{ paddingBottom: 0, height: 'auto' }}>
        <div style={{ textAlign: 'center', padding: '40px', background: '#1a1a1a', borderRadius: '8px' }}>
          <div className="spinner-border text-light" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p style={{ marginTop: '12px', color: 'white' }}>Loading video...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cpd-video-container" style={{ paddingBottom: 0, height: 'auto' }}>
        <div style={{ textAlign: 'center', padding: '40px', background: '#1a1a1a', borderRadius: '8px' }}>
          <i className="bi bi-exclamation-triangle" style={{ fontSize: '3rem', color: '#ffc107' }}></i>
          <p style={{ marginTop: '12px', color: 'white' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cpd-video-container" style={{ paddingBottom: 0, height: 'auto' }}>
      <video controls style={{ width: '100%', maxHeight: '70vh', borderRadius: '8px' }} src={videoSrc}>
        Your browser does not support the video element.
      </video>
    </div>
  );
};

const KnowledgeAreaBadge = ({ area }) => (
  <span
    className="cpd-knowledge-badge"
    style={{ backgroundColor: KNOWLEDGE_AREA_COLOURS[area] || '#6c757d', color: area === 'professionalism' ? '#fff' : undefined }}
  >
    {KNOWLEDGE_AREA_LABELS[area] || area}
  </span>
);

const CpdSummary = ({ year, onYearChange }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const data = await cpdAPI.getMySummary(year);
        setSummary(data);
      } catch {
        setSummary(null);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [year]);

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 3; y--) yearOptions.push(y);

  if (loading) {
    return (
      <div className="cpd-summary-card">
        <div className="text-center py-3">
          <div className="spinner-border spinner-border-sm" style={{ color: 'var(--primary-color)' }} />
        </div>
      </div>
    );
  }

  if (!summary) return null;

  const pct = Math.min((summary.total_hours / summary.target_hours) * 100, 100);

  return (
    <div className="cpd-summary-card">
      <div className="cpd-summary-header">
        <h4>CPD Progress</h4>
        <select
          className="cpd-year-select"
          value={year}
          onChange={e => onYearChange(parseInt(e.target.value))}
        >
          {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      <div className="cpd-summary-total">
        <div className="cpd-summary-label">
          <span>{summary.total_hours} / {summary.target_hours} hours</span>
          <span className="cpd-summary-tasks">{summary.tasks_completed} tasks completed</span>
        </div>
        <div className="cpd-progress-bar">
          <div
            className="cpd-progress-fill"
            style={{ width: `${pct}%`, backgroundColor: pct >= 100 ? 'var(--success-color)' : 'var(--info-color)' }}
          />
        </div>
      </div>

      <div className="cpd-summary-areas">
        {Object.entries(KNOWLEDGE_AREA_LABELS).map(([key, label]) => {
          const hours = summary.by_area[key] || 0;
          return (
            <div key={key} className="cpd-area-row">
              <div className="cpd-area-label">
                <span className="cpd-area-dot" style={{ backgroundColor: KNOWLEDGE_AREA_COLOURS[key] }} />
                <span>{label}</span>
              </div>
              <span className="cpd-area-hours">{hours} hrs</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CpdApp = () => {
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [filter, setFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [summaryYear, setSummaryYear] = useState(new Date().getFullYear());

  // Video / Reading state
  const [videoConfirmed, setVideoConfirmed] = useState(false);
  const [submittingVideo, setSubmittingVideo] = useState(false);
  const [readingConfirmed, setReadingConfirmed] = useState(false);
  const [submittingReading, setSubmittingReading] = useState(false);

  // Quiz state
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);

  // Resource info for video/reading tasks
  const [resourceInfo, setResourceInfo] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    setIsLoading(true);
    try {
      const data = await cpdAPI.getTasks();
      setTasks(data);
    } catch {
      setMessage({ type: 'error', text: 'Failed to load CPD tasks' });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTasks = useMemo(() => {
    if (filter === 'all') return tasks;
    if (filter === 'video') return tasks.filter(t => t.task_type === 'video');
    if (filter === 'quiz') return tasks.filter(t => t.task_type === 'quiz');
    if (filter === 'reading') return tasks.filter(t => t.task_type === 'reading');
    if (filter === 'completed') return tasks.filter(t => t.passed);
    return tasks;
  }, [tasks, filter]);

  const openTask = async (task) => {
    setSelectedTask(task);
    setMessage(null);
    setVideoConfirmed(false);
    setReadingConfirmed(false);
    setQuizResult(null);
    setResourceInfo(null);

    if (task.task_type === 'quiz') {
      try {
        const qs = await cpdAPI.getTaskQuestions(task.id);
        setQuestions(qs);
        setAnswers(new Array(qs.length).fill(-1));
      } catch {
        setQuestions([]);
      }
    }

    if ((task.task_type === 'video' || task.task_type === 'reading') && task.resource_item_id) {
      try {
        const info = await cpdAPI.getResourceInfo(task.id);
        console.log('CPD resource info:', info);
        setResourceInfo(info);
      } catch (err) {
        console.error('CPD resource info fetch failed:', err);
      }
    } else if (task.task_type === 'video') {
      console.warn('Video task has no resource_item_id:', task);
    }
  };

  const goBack = () => {
    setSelectedTask(null);
    setQuestions([]);
    setAnswers([]);
    setQuizResult(null);
    setResourceInfo(null);
    fetchTasks(); // refresh completion status
  };

  // ─── Video Handlers ───────────────────────────────────────────

  const handleCompleteVideo = async () => {
    setSubmittingVideo(true);
    try {
      await cpdAPI.completeVideo(selectedTask.id);
      setSelectedTask(prev => ({ ...prev, completed: true, passed: true }));
      setMessage({ type: 'success', text: 'Video completion recorded!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to record completion' });
    } finally {
      setSubmittingVideo(false);
    }
  };

  // ─── Reading Handlers ─────────────────────────────────────────

  const handleCompleteReading = async () => {
    setSubmittingReading(true);
    try {
      await cpdAPI.completeReading(selectedTask.id);
      setSelectedTask(prev => ({ ...prev, completed: true, passed: true }));
      setMessage({ type: 'success', text: 'Reading completion recorded!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to record completion' });
    } finally {
      setSubmittingReading(false);
    }
  };

  // ─── Quiz Handlers ────────────────────────────────────────────

  const handleQuizSubmit = async () => {
    if (answers.some(a => a === -1)) {
      setMessage({ type: 'error', text: 'Please answer all questions before submitting' });
      return;
    }
    setSubmittingQuiz(true);
    setMessage(null);
    try {
      const result = await cpdAPI.submitQuiz(selectedTask.id, answers);
      setQuizResult(result);
      if (result.passed) {
        setSelectedTask(prev => ({ ...prev, completed: true, passed: true, score: result.score }));
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to submit quiz' });
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const handleRetryQuiz = () => {
    setQuizResult(null);
    setAnswers(new Array(questions.length).fill(-1));
    setMessage(null);
  };

  // ─── Download Handlers ────────────────────────────────────────

  const handleDownloadCertificate = () => {
    const token = localStorage.getItem('access_token');
    const downloadUrl = `${API_BASE_URL}/api/cpd/tasks/${selectedTask.id}/certificate?token=${token}`;
    window.open(downloadUrl, '_blank');
  };

  const handleDownloadCaseStudy = () => {
    const token = localStorage.getItem('access_token');
    const downloadUrl = `${API_BASE_URL}/api/cpd/tasks/${selectedTask.id}/case-study?token=${token}`;
    window.open(downloadUrl, '_blank');
  };

  // ─── Video Embed ──────────────────────────────────────────────

  const renderVideoEmbed = () => {
    if (!resourceInfo) return <p className="text-muted">No video linked to this task.</p>;

    let metadata = {};
    if (resourceInfo.preview_metadata) {
      try {
        metadata = typeof resourceInfo.preview_metadata === 'string'
          ? JSON.parse(resourceInfo.preview_metadata)
          : resourceInfo.preview_metadata;
      } catch { /* ignore */ }
    }

    if (resourceInfo.preview_type === 'youtube' && metadata.video_id) {
      return (
        <div className="cpd-video-container">
          <iframe
            src={`https://www.youtube.com/embed/${metadata.video_id}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={selectedTask.title}
          />
        </div>
      );
    }

    if (resourceInfo.preview_type === 'vimeo' && metadata.video_id) {
      return (
        <div className="cpd-video-container">
          <iframe
            src={`https://player.vimeo.com/video/${metadata.video_id}`}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title={selectedTask.title}
          />
        </div>
      );
    }

    if (resourceInfo.preview_type === 'video' && resourceInfo.id) {
      return <CpdNativeVideo resourceId={resourceInfo.id} title={selectedTask.title} />;
    }

    return <p className="text-muted">
      Video preview not available (resource type: {resourceInfo.preview_type || resourceInfo.item_type || 'unknown'}).
      Please link a video resource to this task.
    </p>;
  };

  // ─── Render ───────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="cpd-app text-center py-5">
        <div className="spinner-border" style={{ color: 'var(--primary-color)' }} />
      </div>
    );
  }

  // Detail View
  if (selectedTask) {
    return (
      <div className="cpd-app cpd-detail">
        <button className="back-btn" onClick={goBack}>&larr; Back to all tasks</button>

        <div className="d-flex align-items-center gap-3 mb-3">
          <h3 className="mb-0">{selectedTask.title}</h3>
          <span className={`cpd-type-badge ${selectedTask.task_type}`}>
            {selectedTask.task_type === 'video' ? 'Video' : selectedTask.task_type === 'reading' ? 'Reading' : 'Quiz'}
          </span>
        </div>
        <p className="text-muted" style={{ fontSize: '0.85rem' }}>
          FAA: {selectedTask.faa_number} &nbsp;·&nbsp; {selectedTask.cpd_hours ?? 1} CPD {(selectedTask.cpd_hours ?? 1) === 1 ? 'hr' : 'hrs'}
          &nbsp;·&nbsp; <KnowledgeAreaBadge area={selectedTask.knowledge_area || 'technical'} />
        </p>
        {selectedTask.description && <p>{selectedTask.description}</p>}

        {message && (
          <div className={`alert alert-${message.type === 'error' ? 'danger' : 'success'} alert-dismissible`}>
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage(null)} />
          </div>
        )}

        {/* ─── Video Task ─── */}
        {selectedTask.task_type === 'video' && (
          <>
            {renderVideoEmbed()}

            {selectedTask.passed ? (
              <div className="cpd-result-box passed">
                <h4>Completed</h4>
                <p>You have completed this video CPD task.</p>
                {selectedTask.certificate_filename && (
                  <button className="cpd-btn-secondary" onClick={handleDownloadCertificate}>
                    Download Certificate
                  </button>
                )}
              </div>
            ) : (
              <div className="mt-3">
                <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={videoConfirmed}
                    onChange={e => setVideoConfirmed(e.target.checked)}
                    style={{ width: 18, height: 18 }}
                  />
                  <span>I confirm I have watched this video in its entirety</span>
                </label>
                <button
                  className="cpd-btn-primary mt-3"
                  disabled={!videoConfirmed || submittingVideo}
                  onClick={handleCompleteVideo}
                >
                  {submittingVideo ? 'Submitting...' : 'Submit Completion'}
                </button>
              </div>
            )}
          </>
        )}

        {/* ─── Reading Task ─── */}
        {selectedTask.task_type === 'reading' && (
          <>
            {resourceInfo && resourceInfo.id ? (
              <div className="mb-3">
                <button className="cpd-btn-primary" onClick={() => setShowPdfModal(true)}>
                  Open Document
                </button>
                <PDFPreviewModal
                  isOpen={showPdfModal}
                  onClose={() => setShowPdfModal(false)}
                  pdfUrl={`${API_BASE_URL}/api/resources/download/${resourceInfo.id}`}
                  title={selectedTask.title}
                  token={localStorage.getItem('access_token')}
                />
              </div>
            ) : (
              <p className="text-muted">No document linked to this task.</p>
            )}

            {selectedTask.passed ? (
              <div className="cpd-result-box passed">
                <h4>Completed</h4>
                <p>You have completed this reading CPD task.</p>
                {selectedTask.certificate_filename && (
                  <button className="cpd-btn-secondary" onClick={handleDownloadCertificate}>
                    Download Certificate
                  </button>
                )}
              </div>
            ) : (
              <div className="mt-3">
                <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={readingConfirmed}
                    onChange={e => setReadingConfirmed(e.target.checked)}
                    style={{ width: 18, height: 18 }}
                  />
                  <span>I confirm I have read this document in its entirety</span>
                </label>
                <button
                  className="cpd-btn-primary mt-3"
                  disabled={!readingConfirmed || submittingReading}
                  onClick={handleCompleteReading}
                >
                  {submittingReading ? 'Submitting...' : 'Submit Completion'}
                </button>
              </div>
            )}
          </>
        )}

        {/* ─── Quiz Task ─── */}
        {selectedTask.task_type === 'quiz' && (
          <>
            {selectedTask.case_study_filename && (
              <button className="cpd-btn-secondary mb-3" onClick={handleDownloadCaseStudy}>
                Download Case Study
              </button>
            )}

            {quizResult ? (
              <div className={`cpd-result-box ${quizResult.passed ? 'passed' : 'failed'}`}>
                <h4>{quizResult.passed ? 'Passed!' : 'Not Passed'}</h4>
                <p>
                  Your score: <strong>{quizResult.score}%</strong> (pass mark: {quizResult.pass_mark}%)
                </p>
                {quizResult.passed ? (
                  selectedTask.certificate_filename && (
                    <button className="cpd-btn-secondary" onClick={handleDownloadCertificate}>
                      Download Certificate
                    </button>
                  )
                ) : (
                  <button className="cpd-btn-primary" onClick={handleRetryQuiz}>
                    Try Again
                  </button>
                )}
              </div>
            ) : (
              <div className="cpd-quiz-form">
                {questions.length === 0 ? (
                  <p className="text-muted">No questions available for this quiz yet.</p>
                ) : (
                  <>
                    {questions.map((q, qi) => {
                      const hasPassed = selectedTask.passed && q.correct_option !== null && q.correct_option !== undefined;
                      return (
                        <div key={q.id} className="cpd-question-card">
                          <h6>Q{qi + 1}. {q.question_text}</h6>
                          {q.options.map((opt, oi) => {
                            const isCorrect = hasPassed && oi === q.correct_option;
                            return (
                              <label
                                key={oi}
                                className={`option-label${isCorrect ? ' correct-answer' : ''}`}
                                style={isCorrect ? { background: 'var(--success-color)', borderRadius: '6px', fontWeight: 600 } : {}}
                              >
                                <input
                                  type="radio"
                                  name={`q_${q.id}`}
                                  checked={hasPassed ? oi === q.correct_option : answers[qi] === oi}
                                  disabled={hasPassed}
                                  onChange={() => {
                                    const newAnswers = [...answers];
                                    newAnswers[qi] = oi;
                                    setAnswers(newAnswers);
                                  }}
                                />
                                <span>{String.fromCharCode(65 + oi)}. {opt}</span>
                              </label>
                            );
                          })}
                        </div>
                      );
                    })}
                    {!selectedTask.passed && (
                      <button
                        className="cpd-btn-primary"
                        onClick={handleQuizSubmit}
                        disabled={submittingQuiz}
                      >
                        {submittingQuiz ? 'Submitting...' : 'Submit Answers'}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Show certificate download if already passed (coming back to task) */}
            {selectedTask.passed && !quizResult && selectedTask.certificate_filename && (
              <div className="cpd-result-box passed mt-3">
                <h4>Previously Passed</h4>
                <p>Score: <strong>{selectedTask.score}%</strong></p>
                <button className="cpd-btn-secondary" onClick={handleDownloadCertificate}>
                  Download Certificate
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // List View
  return (
    <div className="cpd-app">
      <h2>Continuing Professional Development</h2>

      <CpdSummary year={summaryYear} onYearChange={setSummaryYear} />

      <div className="cpd-filter-tabs">
        {['all', 'video', 'reading', 'quiz', 'completed'].map(f => (
          <button
            key={f}
            className={filter === f ? 'active' : ''}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : f === 'video' ? 'Video' : f === 'reading' ? 'Reading' : f === 'quiz' ? 'Quiz' : 'Completed'}
          </button>
        ))}
      </div>

      {filteredTasks.length === 0 ? (
        <p className="text-muted">No CPD tasks found.</p>
      ) : (
        filteredTasks.map(task => (
          <div key={task.id} className="cpd-task-card" onClick={() => openTask(task)}>
            <div className="task-info">
              <h5>{task.title}</h5>
              <span className="faa-number">FAA: {task.faa_number}</span>
            </div>
            <div className="task-meta">
              <span className={`cpd-type-badge ${task.task_type}`}>
                {task.task_type === 'video' ? 'Video' : task.task_type === 'reading' ? 'Reading' : 'Quiz'}
              </span>
              <KnowledgeAreaBadge area={task.knowledge_area || 'technical'} />
              <span className="cpd-hours-badge">
                {task.cpd_hours ?? 1} {(task.cpd_hours ?? 1) === 1 ? 'hr' : 'hrs'}
              </span>
              <span className="cpd-status-icon">
                {task.passed ? '✅' : '○'}
              </span>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default CpdApp;
